import React from "react";
import { X, Clock, Lightbulb, ArrowRight } from "lucide-react";
import Modal from "../modal/Modal";

const CourseDetailsModal = ({ isOpen, onClose, course }) => {
  if (!course) return null;

  const getDifficultyColor = (difficulty) => {
    const colors = {
      beginner: "bg-green-100 text-green-800",
      intermediate: "bg-yellow-100 text-yellow-800", 
      advanced: "bg-red-100 text-red-800",
    };
    return colors[difficulty?.toLowerCase()] || colors.beginner;
  };

  const skills = course.skills || ["AI Literacy", "Machine Learning Basics", "Data Analysis"];
  const duration = course.duration || "2 weeks";
  const courseType = course.courseType || "Course";
  const skillsCount = skills.length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="w-[700px]">
      <div className="p-2 space-y-3">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-lg font-bold text-blue-900 mb-1">
              {course.title}
            </h2>
            <p className="text-sm text-gray-500">
              {course.description}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div>
              <span className="text-sm font-medium text-gray-500">Duration</span>
              <p className="text-gray-700">{duration}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Course Type</span>
              <p className="text-gray-700">{courseType}</p>
            </div>
          </div>
          <div className="space-y-2">
            <div>
              <span className="text-sm font-medium text-gray-500">Difficulty Level</span>
              <div className="mt-1">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(course.difficulty)}`}>
                  {course.difficulty || "Beginner"}
                </span>
              </div>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Skills Covered</span>
              <p className="text-gray-700">{skillsCount} skills</p>
            </div>
          </div>
        </div>

        {/* Skills You'll Learn */}
        <div>
          <h3 className="text-sm font-bold text-gray-700">Skills You'll Learn</h3>
          <div className="flex flex-wrap gap-2">
            {skills.map((skill, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>

        {/* Recommendation Section */}
        <div className="flex items-start gap-2">
          <Lightbulb className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-gray-600">
              <span className="font-semibold">Why This Course is Recommended:</span>{" "}
              {course.recommendationReason || "Establish core AI foundations aligned with your target role."}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 mb-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          <button
            onClick={() => {
              if (course.link) {
                window.open(course.link, '_blank');
              }
              onClose();
            }}
            className="px-6 py-2 mb-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            Start Course
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CourseDetailsModal;